import { updateUserDetail, UserCombinePermissions } from '@back-office/http';
import _ from 'lodash';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GcpUserTableProps, PopupState, PopupStyle } from '.';
import {
  ISD_CODE_LENGTH,
  PH_ISD_CODE,
  PH_MOBILE_NUMBER_LENGTH,
} from '../../common/constants';
import { usePagination } from '../../hooks';
import useInject from '../../hooks/useInject';
import { UserRole } from '../gcp-tab';

type UseViewModelReturn = {
  getRoleColor: (role: string) => 'hitGray' | 'black';
  handleEditButtonClick: (user: UserCombinePermissions) => Promise<void>;
  handleSaveButtonClick: (user: UserCombinePermissions) => void;
  handlePhoneNumberFieldChange: (
    val: ChangeEvent<HTMLInputElement>,
    phoneNumber: string
  ) => void;
  handlePermissionFieldChange: (
    permissionId: number | number[],
    newPermissionId: number[],
    isPermissionLimited: boolean
  ) => void;
  handleChangePage: (
    event: React.ChangeEvent<unknown> | null,
    newPage: number,
    cb?: (isNext: boolean) => void
  ) => void;
  getIconStyle: () => React.CSSProperties | undefined;
  getIconStyleTrash: () => React.CSSProperties | undefined;
  anyEditingRow: () => boolean;
  enableSaveButton: boolean;
  editedPhoneNumber: string | undefined;
  popupState: PopupState | undefined;
  getPopupProps: () => PopupStyle;
  sortUserRoleAndPermission: (
    users: UserCombinePermissions[]
  ) => UserCombinePermissions[];
  page: number;
  rowsPerPage: number;
  editingRow: Map<string, boolean>;
  isAdmin: (role: string) => boolean;
  convertToArray: (value: number | number[]) => number[];
  setEditingRow: React.Dispatch<React.SetStateAction<Map<string, boolean>>>;
  setEnableSaveButton: React.Dispatch<React.SetStateAction<boolean>>;
};

const useViewModel = ({
  isRecurringMerchant,
  refresh,
}: Omit<
  GcpUserTableProps,
  'data' | 'permissionsList' | 'handleOnClickRemove'
>): UseViewModelReturn => {
  const { alert } = useInject((store) => store);
  const { t } = useTranslation();
  const { handleChangePage, page, rowsPerPage } = usePagination();
  const [editingRow, setEditingRow] = useState<Map<string, boolean>>(new Map());
  const [enableSaveButton, setEnableSaveButton] = useState<boolean>(false);
  const [popupState, setPopupState] = useState<PopupState>();
  const [editedPhoneNumber, setEditedPhoneNumber] = useState<string>();
  const [editedPermission, setEditedPermission] = useState<number[]>();
  const [phoneAndPermissionEnableSave, setPhoneAndPermissionEnableSave] =
    useState<{
      phone: boolean;
      permission: boolean;
    }>({ phone: false, permission: false });

  const isAdmin = (role: string): boolean => role === UserRole.admin;

  const getRoleColor = (role: string): 'hitGray' | 'black' => {
    if (isAdmin(role)) {
      return 'hitGray';
    }
    return 'black';
  };

  const handleEditButtonClick = async (
    user: UserCombinePermissions
  ): Promise<void> => {
    const phoneNumber = isPhoneNumberWithCode(user.phone)
      ? user.phone.substring(ISD_CODE_LENGTH)
      : user.phone;

    setEnableSaveButton(false);
    setEditedPhoneNumber(phoneNumber);
    setEditingRow(new Map([[user.email, true]]));
    setPhoneAndPermissionEnableSave({ phone: false, permission: false });
  };

  const anyEditingRow = (): boolean => editingRow.size !== 0;

  const getIconStyle = (): React.CSSProperties | undefined =>
    anyEditingRow() ? { color: '#E0E0E0' } : undefined;

  const getIconStyleTrash = (): React.CSSProperties | undefined =>
    anyEditingRow() ? { stroke: '#E0E0E0' } : undefined;

  const handleSaveButtonClick = (user: UserCombinePermissions): void => {
    alert.openAlert({
      cancelBtn: 'dialog.cancel',
      okBtn: 'dialog.confirm',
      title: t('invite_new_user.edit_confirmation'),
      message: t('invite_new_user.edit_confirmation_content'),
      handleCancel() {
        alert.closeDialog();
      },

      async handleOK() {
        await updateUserDetail(
          user,
          `${PH_ISD_CODE}${editedPhoneNumber}`,
          editedPermission
        )
          .then(({ data: { status } }) => showPopup(status.code === 1000))
          .catch(() => showPopup(false))
          .finally(() => alert.closeDialog());
      },
    });
  };

  const showPopup = (isSuccess: boolean): void => {
    setPopupState({ show: true, isSuccess } as PopupState);
    refresh();
    setEditingRow(new Map());
    const timeoutID = setInterval(() => {
      clearInterval(timeoutID);
      setPopupState({ show: false, isSuccess: true } as PopupState);
    }, 5000);
  };

  const isPhoneNumberWithCode = (phoneNumber: string): boolean => {
    if (phoneNumber.includes('+') && phoneNumber.length > ISD_CODE_LENGTH) {
      return true;
    }
    return false;
  };

  const handlePhoneNumberFieldChange = (
    val: ChangeEvent<HTMLInputElement>,
    phoneNumber: string
  ): void => {
    const regex = new RegExp(`^[0-9]{${PH_MOBILE_NUMBER_LENGTH}}$`);
    const phoneNumberWithoutCode = phoneNumber.slice(3);
    setPhoneAndPermissionEnableSave({
      ...phoneAndPermissionEnableSave,
      phone:
        regex.test(val.target.value) &&
        val.target.value !== phoneNumberWithoutCode,
    });
    setEditedPhoneNumber(val.target.value);
  };

  const getPopupProps = (): PopupStyle => {
    if (popupState?.isSuccess) {
      return {
        backgroundColor: '#e8f5ee',
        message: t('invite_new_user.update_success_message'),
      };
    }
    return {
      backgroundColor: '#fdeeee',
      message: t('invite_new_user.update_fail_message'),
    };
  };

  const handlePermissionFieldChange = (
    permissionId: number | number[],
    newPermissionId: number[],
    isPermissionLimited: boolean
  ): void => {
    setPhoneAndPermissionEnableSave({
      ...phoneAndPermissionEnableSave,
      permission:
        !_.isEqual(
          [...convertToArray(permissionId)].sort((a, b) => a - b),
          [...newPermissionId].sort((a, b) => a - b)
        ) && !isPermissionLimited,
    });

    setEditedPermission(newPermissionId);
  };

  const convertToArray = (value: number | number[]): number[] =>
    // eslint-disable-next-line no-nested-ternary
    _.isNumber(value) ? [value] : _.isNull(value) ? [0] : value;

  useEffect(() => {
    setEnableSaveButton(
      phoneAndPermissionEnableSave.permission ||
        phoneAndPermissionEnableSave.phone
    );
  }, [phoneAndPermissionEnableSave]);

  const sortUserRoleAndPermission = (
    users: UserCombinePermissions[]
  ): UserCombinePermissions[] =>
    _.sortBy(users, [
      (user: UserCombinePermissions) => {
        if (isRecurringMerchant) {
          if (user.role === 'ADMIN') return 0;
          return user.permissionId && user.permissionId.length > 0 ? 1 : 2;
        }
        if (user.role === 'ADMIN') return 0;
        return user.permissionId && user.permissionId.includes(1) ? 1 : 2;
      },
    ]);

  return {
    getRoleColor,
    handleEditButtonClick,
    handleSaveButtonClick,
    handlePhoneNumberFieldChange,
    handlePermissionFieldChange,
    handleChangePage,
    getIconStyle,
    getIconStyleTrash,
    anyEditingRow,
    enableSaveButton,
    editedPhoneNumber,
    popupState,
    getPopupProps,
    sortUserRoleAndPermission,
    page,
    rowsPerPage,
    editingRow,
    isAdmin,
    convertToArray,
    setEditingRow,
    setEnableSaveButton,
  };
};

export default useViewModel;
